import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useQuery } from "@apollo/client";
import { GET_CONTRACT } from "../../graphql/core";
import ScreenCard, { ButtonType, ScreenCardOptionalAction } from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { ContractSignatureStatus, ProfileType } from "../../__generated__/graphql";
import Loading from "../../components/Loading";
import PdfAssetViewer from "../../components/PdfAssetViewer";
import { useNavigationHeader, useShowHideHeader } from "../../hooks/use-navigation-header";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { Platform, View } from "react-native";
import MarkdownView from "../../components/MarkdownView";
import { isScrollEnabledForPdfComponent } from "../../utils/pdf-scroll";

type ContractPreviewScreenProps = StackScreenProps<RootStackParamList, "ContractPreviewScreen">;

type ButtonsConfig = {
    canSign?: boolean;
    canSignOther?: boolean; // this is used when there is multiple employees in the client
    canSignLater?: boolean;
    canClose?: boolean;
};

/**
 * This screen is used to check if the contract is ready to be signed
 * [ButtonsConfig, labelText, infoText]
 */
const ERRONEOUS_VALUE: [ButtonsConfig, string?] = [{ canClose: true }, undefined];

const STATUS_BUTTON_MAP: Record<ContractSignatureStatus | "none", [ButtonsConfig?, string?]> = {
    none: [],
    canceled_by_lse: ERRONEOUS_VALUE,
    canceled_by_other: ERRONEOUS_VALUE,
    canceled_by_self: ERRONEOUS_VALUE,
    failed: ERRONEOUS_VALUE,
    need_other_sign: [{ canClose: true }, "screens.contract_preview.other_sign_info"],
    need_seal: ERRONEOUS_VALUE,
    need_self_sign: [{ canSign: true, canSignLater: true, canSignOther: true }, undefined],
    waiting_self_sign: [{ canSign: true, canSignLater: true, canSignOther: true }, undefined],
    success: [{ canClose: true }, "screens.contract_preview.invalid_info"],
    waiting_contract: [undefined, "screens.contract_preview.valid_info"],
};

const ContractPreviewScreen: React.FC<ContractPreviewScreenProps> = ({ route, navigation }) => {
    const { profileType } = useContext(ProfileSwitcherManagerContext);

    const i18n = useI18n();
    const [clientAssetId, setClientAssetId] = useState<string | null>(null);
    const { data, error, loading } = useQuery(GET_CONTRACT, {
        variables: { id: route.params.contractId },
    });

    useEffect(() => {
        const clientAssetIdFromContract = data?.getContract?.contractEffectiveAsset?.id;
        if (
            !clientAssetId &&
            clientAssetIdFromContract &&
            clientAssetIdFromContract !== clientAssetId
        ) {
            setClientAssetId(clientAssetIdFromContract);
        }
    }, [clientAssetId, data?.getContract?.contractEffectiveAsset?.id]);

    const signatureStatus = data?.getContract?.signatureStatus;

    const [buttonsConfig, infoText] = STATUS_BUTTON_MAP[signatureStatus || "none"];

    const signNow = useCallback(() => {
        navigation.navigate("ContractSigningScreen", { contractId: route.params.contractId });
    }, [navigation, route.params.contractId]);

    const signLater = useCallback(() => {
        navigation.popTo("Home", {
            screen: "MissionsScreen",
            params: {
                screen: "MissionDetails",
                params: {
                    missionId: data?.getContract?.missionInfo?.id || "",
                },
            },
        });
    }, [data?.getContract?.missionInfo?.id, navigation]);

    const close = useCallback(() => {
        navigation.reset({
            index: 0,
            routes: [{ name: "Home" }],
        });
    }, [navigation]);

    const signOther = useCallback(() => {
        navigation.navigate("MissionSelectSignerScreen", {
            missionId: data?.getContract?.missionInfo?.id || "",
        });
    }, [data?.getContract?.missionInfo?.id, navigation]);

    const [buttons, optionalActions] = useMemo<[ButtonType[], ScreenCardOptionalAction[]]>(() => {
        const _all = [
            ...(buttonsConfig?.canSign
                ? [
                      {
                          title: i18n.t("screens.contract_preview.sign_contract"),
                          mode: "secondary" as const,
                          validating: loading,
                          onPress: signNow,
                      },
                  ]
                : []),
            ...(buttonsConfig?.canSignLater
                ? [
                      {
                          title: i18n.t("screens.contract_preview.postpone_signing"),
                          mode: "outlined" as const,
                          onPress: signLater,
                      },
                  ]
                : []),
            ...(buttonsConfig?.canSignOther && profileType === ProfileType.Client
                ? [
                      {
                          title: i18n.t("screens.contract_preview.other_sign_contract"),
                          mode: "outlined" as const,
                          onPress: signOther,
                      },
                  ]
                : []),
            ...(buttonsConfig?.canClose
                ? [
                      {
                          title: i18n.t("close"),
                          mode: "outlined" as const,
                          onPress: close,
                      },
                  ]
                : []),
        ];

        if (_all.length > 2) {
            const other = _all.splice(1).map((b) => ({
                label: b.title,
                onPress: b.onPress,
            }));
            const first = _all[0];
            return [[first], other];
        }
        return [_all, []];
    }, [
        buttonsConfig?.canClose,
        buttonsConfig?.canSign,
        buttonsConfig?.canSignLater,
        buttonsConfig?.canSignOther,
        close,
        i18n,
        loading,
        profileType,
        signLater,
        signNow,
        signOther,
    ]);

    useNavigationHeader(
        profileType == ProfileType.Client
            ? i18n.t("contract.pdf_prefix.client")
            : i18n.t("contract.pdf_prefix.worker"),
    );

    // Hide the header on web to avoid conflicts with the PDF viewer
    useShowHideHeader(Platform.OS === "web" ? "hide" : undefined);

    return (
        <ScreenCard
            optionalActions={optionalActions}
            buttons={buttons}
            headband={
                error
                    ? {
                          text: i18n.t("screens.contract_preview.error"),
                          mode: "error",
                      }
                    : undefined
            }
            scrollEnabled={isScrollEnabledForPdfComponent("ScreenCard")}
            isContentContainerFlex
        >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {(paddingBottom: number) => {
                return clientAssetId && buttonsConfig?.canSign ? (
                    <PdfAssetViewer
                        assetId={clientAssetId}
                        waitingDescription={i18n.t("screens.contract_preview.loading_document")}
                        style={{ paddingBottom }}
                        scrollEnabled={isScrollEnabledForPdfComponent("PdfViewer")}
                    />
                ) : infoText ? (
                    <View style={{ flex: 1, justifyContent: "space-evenly" }}>
                        {infoText && (
                            <MarkdownView
                                markdownStyle={{
                                    textgroup: { textAlign: "center" },
                                }}
                                markdown={i18n.t(infoText)}
                            />
                        )}
                    </View>
                ) : (
                    <Loading />
                );
            }}
        </ScreenCard>
    );
};

export default ContractPreviewScreen;
